/**
 * Página de produto: galeria, opções e quantidades.
 *
 * Os preços das quantidades saem do preço real da variante escolhida. Nada é
 * escrito à mão: trocar de cor ou de quantidade recalcula tudo a partir dos
 * dados que a loja já enviou com a página.
 */

/**
 * O formato do dinheiro vem da loja, não daqui. Inventá-lo deixava a página
 * com "17,90 €" num sítio e "€39,80" noutro, na mesma linha de cartões.
 */
function dinheiro(centimos, formato) {
  const valor = (centimos / 100).toLocaleString('pt-PT', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

  if (!formato) return `${valor} €`;

  // A loja escreve o padrão com um marcador entre chaves duplas
  return formato.replace(/\{\{\s*\w+\s*\}\}/, valor);
}

class ClaroProduto extends HTMLElement {
  #variantes = [];
  #variante = null;
  #qtd = 1;

  connectedCallback() {
    this.formato = this.dataset.formato || '';

    try {
      this.#variantes = JSON.parse(this.dataset.variantes || '[]');
    } catch {
      this.#variantes = [];
    }

    this.#variante = this.#variantes.find((v) => v.available) || this.#variantes[0] || null;

    // Guardado no arranque: depois de uma troca já não se sabe qual era
    this.textoBotao = this.querySelector('[data-claro-add-texto]')?.textContent.trim() ?? '';

    this.#ligaGaleria();
    this.#ligaOpcoes();
    this.#ligaQuantidades();
    this.#ligaExtra();
    this.#ligaJuntos();
    this.#ligaFixa();
    this.#ligaRelogio();
    this.#pinta();
  }

  #euros(centimos) {
    return dinheiro(centimos, this.formato);
  }

  /* ── o relógio ────────────────────────────────────────────
     Conta para a hora de corte dos envios do dia. Passada essa hora, esconde-se
     em vez de recomeçar — um relógio que reinicia não conta nada. */
  #ligaRelogio() {
    const caixa = this.querySelector('[data-claro-corte]');
    const mostrador = this.querySelector('[data-claro-relogio]');
    if (!caixa || !mostrador) return;

    const [hora, minuto] = (caixa.dataset.claroCorte || '').split(':').map(Number);
    if (Number.isNaN(hora)) return;

    const bater = () => {
      const agora = new Date();
      const corte = new Date(agora);
      corte.setHours(hora, minuto || 0, 0, 0);

      const falta = corte - agora;
      if (falta <= 0) {
        caixa.hidden = true;
        return false;
      }

      const h = Math.floor(falta / 3600000);
      const m = Math.floor((falta % 3600000) / 60000);
      const sg = Math.floor((falta % 60000) / 1000);
      mostrador.textContent = [h, m, sg].map((n) => String(n).padStart(2, '0')).join(':');
      caixa.hidden = false;
      return true;
    };

    if (bater()) setInterval(bater, 1000);
  }

  /* ── galeria ── */
  #ligaGaleria() {
    this.tiras = [...this.querySelectorAll('[data-claro-tira]')];
    this.fotos = [...this.querySelectorAll('[data-claro-foto]')];
    this.conta = this.querySelector('[data-claro-conta]');

    this.tiras.forEach((tira) =>
      tira.addEventListener('click', () => this.#mostraFoto(Number(tira.dataset.claroTira)))
    );
  }

  /** Passa a galeria para uma fotografia, venha o pedido de onde vier */
  #mostraFoto(i) {
    if (!this.fotos?.length || i < 0 || i >= this.fotos.length) return;

    this.fotos.forEach((f) => (f.hidden = Number(f.dataset.claroFoto) !== i));
    this.tiras.forEach((t) => t.setAttribute('aria-current', String(Number(t.dataset.claroTira) === i)));
    if (this.conta) this.conta.textContent = `${i + 1} / ${this.fotos.length}`;
  }

  /* ── opções ──────────────────────────────────────────────
     Duas formas para a mesma coisa: pastilhas quando há cor para mostrar,
     lista de escolha quando não há. */
  #ligaOpcoes() {
    this.querySelectorAll('[data-claro-opcao]').forEach((grupo) => {
      grupo.querySelectorAll('[data-claro-valor]').forEach((botao) =>
        botao.addEventListener('click', () => {
          grupo
            .querySelectorAll('[data-claro-valor]')
            .forEach((b) => b.setAttribute('aria-pressed', String(b === botao)));
          this.#escolheVariante();
        })
      );

      grupo
        .querySelector('[data-claro-sel]')
        ?.addEventListener('change', () => this.#escolheVariante());
    });
  }

  /** O valor escolhido de um grupo, venha ele de pastilha ou de lista */
  #valorDe(grupo) {
    const lista = grupo.querySelector('[data-claro-sel]');
    if (lista) return lista.value;
    return grupo.querySelector('[aria-pressed="true"]')?.dataset.claroValor ?? null;
  }

  /** A variante é a que casa com todos os valores escolhidos */
  #escolheVariante() {
    const escolhidos = [...this.querySelectorAll('[data-claro-opcao]')].map((g) =>
      this.#valorDe(g)
    );

    this.#variante =
      this.#variantes.find((v) =>
        escolhidos.every((valor, i) => valor === null || v.options[i] === valor)
      ) || this.#variante;

    this.#pinta();
  }

  /* ── quantidades ── */
  #ligaQuantidades() {
    const cartoes = [...this.querySelectorAll('[role="radio"]')];
    cartoes.forEach((cartao) =>
      cartao.addEventListener('click', () => {
        cartoes.forEach((c) => c.setAttribute('aria-checked', String(c === cartao)));

        // O cartão do pack guarda o artigo a juntar; os outros, a quantidade
        this.packExtra = cartao.dataset.claroPack ? Number(cartao.dataset.claroPack) : null;
        this.#qtd = this.packExtra ? 1 : Number(cartao.dataset.claroQtd) || 1;

        const campo = this.querySelector('[data-claro-qtd-campo]');
        if (campo) campo.value = this.#qtd;
      })
    );
  }

  /* ── comprados juntos ─────────────────────────────────────
     Cada botão marca-se a si próprio. Quem estiver marcado na altura de
     submeter vai no mesmo pedido que o produto principal. */
  #ligaJuntos() {
    this.querySelectorAll('[data-claro-junto]').forEach((botao) =>
      botao.addEventListener('click', () => {
        const dentro = botao.getAttribute('aria-pressed') === 'true';
        botao.setAttribute('aria-pressed', String(!dentro));
        botao.textContent = !dentro
          ? botao.dataset.textoOn || 'Juntado'
          : botao.dataset.textoOff || '+ Juntar';
      })
    );
  }

  /**
   * Os extras escolhidos (pack, artigo a juntar, comprados juntos), para quem
   * faz o envio por conta própria — a capa personalizada.
   * @returns {number[]}
   */
  extrasEscolhidos() {
    return this.#extras();
  }

  /** Tudo o que vai no carrinho além do produto principal */
  #extras() {
    const ids = [];

    if (this.packExtra) ids.push(this.packExtra);

    const extra = this.querySelector('[data-claro-extra]');
    if (extra?.checked) ids.push(Number(extra.value));

    this.querySelectorAll('[data-claro-junto][aria-pressed="true"]').forEach((b) =>
      ids.push(Number(b.dataset.claroJunto))
    );

    // Um artigo escolhido duas vezes chegava ao carrinho como duas linhas iguais
    return [...new Set(ids)];
  }

  /* ── o cartão que segue a página ──────────────────────────
     Aparece quando a imagem do produto sai do ecrã. Enquanto ela lá está, a
     compra está à mão e o cartão só tapava o que se estava a ver.

     O botão manda o mesmo formulário que o de cima — com a variante, a
     quantidade e os extras escolhidos — em vez de levar de volta lá acima. */
  #ligaFixa() {
    const cartao = this.querySelector('[data-claro-fixa]');
    // O bloco todo, não a galeria: ela está colada ao topo e nunca sai do
    // ecrã por si. A imagem só desaparece quando o bloco acaba.
    const bloco = this.querySelector('.claro-prod') || this;
    const form = this.querySelector('form');
    if (!cartao) return;

    this.fixaBotao = cartao.querySelector('[data-claro-fixa-bt]');
    this.fixaBotao?.addEventListener('click', () => {
      const principal = this.querySelector('[data-claro-add]');
      if (principal?.disabled) return;
      form?.requestSubmit(principal);
    });

    new IntersectionObserver(
      ([entrada]) => {
        cartao.toggleAttribute('data-visivel', !entrada.isIntersecting);
      },
      { threshold: 0 }
    ).observe(bloco);
  }

  /* ── o extra ──────────────────────────────────────────────
     O formulário normal só leva uma linha. Com o extra marcado, é preciso
     mandar as duas de uma vez — e aí passa a ser um pedido nosso, não do
     navegador. Sem o extra, deixa-se o formulário seguir como sempre. */
  #ligaExtra() {
    const form = this.querySelector('form');
    if (!form) return;

    form.addEventListener('submit', async (evento) => {
      const juntar = this.#extras();
      if (!juntar.length) return;
      evento.preventDefault();

      const botao = this.querySelector('[data-claro-add]');
      botao.setAttribute('aria-busy', 'true');

      const url = window.Theme?.routes?.cart_add_url || '/cart/add';
      const extras = juntar.map((id) => ({ id, quantity: 1 }));

      // Uma fotografia não viaja em JSON. Com ficheiros no formulário (a capa
      // personalizada), o artigo principal segue no próprio formulário — com
      // a variante, a quantidade e a foto — e os extras vão a seguir, à parte.
      const comFicheiro = [...form.querySelectorAll('input[type="file"]')].some(
        (campo) => campo.files?.length
      );
      const pedidos = comFicheiro
        ? [{ corpo: new FormData(form) }, { corpo: JSON.stringify({ items: extras }), json: true }]
        : [
            {
              corpo: JSON.stringify({ items: [{ id: this.#variante.id, quantity: this.#qtd }, ...extras] }),
              json: true,
            },
          ];

      let principalNoCarrinho = false;

      try {
        for (const pedido of pedidos) {
          const resposta = await fetch(url, {
            method: 'POST',
            headers: pedido.json
              ? { 'Content-Type': 'application/json', Accept: 'application/json' }
              : { Accept: 'application/json' },
            body: pedido.corpo,
          });

          const resultado = await resposta.json();
          if (resultado.status) throw new Error(resultado.description || resultado.message);
          principalNoCarrinho = true;
        }

        window.location.href = window.Theme?.routes?.cart_url || '/cart';
      } catch (erro) {
        console.error('claro-produto:', erro);
        botao.removeAttribute('aria-busy');

        // Se o principal já entrou, mandá-lo outra vez duplicava a linha
        if (principalNoCarrinho) {
          window.location.href = window.Theme?.routes?.cart_url || '/cart';
          return;
        }
        form.submit(); // o caminho normal, com o produto principal apenas
      }
    });
  }

  /* ── desenhar ── */
  #pinta() {
    const v = this.#variante;
    if (!v) return;

    this.querySelector('[data-claro-id]').value = v.id;

    // A fotografia segue a variante escolhida: quem escolhe o preto quer ver
    // o preto. A posição vem da loja e conta a partir de 1.
    const posicao = v.featured_media?.position ?? v.featured_image?.position;
    if (posicao) this.#mostraFoto(posicao - 1);

    const temAntes = v.compare_at_price && v.compare_at_price > v.price;

    const preco = this.querySelector('[data-claro-preco]');
    const antes = this.querySelector('[data-claro-antes]');
    const pct = this.querySelector('[data-claro-pct]');

    if (preco) preco.textContent = this.#euros(v.price);
    if (antes) antes.hidden = !temAntes;
    if (pct) pct.hidden = !temAntes;

    // O selo mostra o dinheiro poupado, com a etiqueta, como a página o desenha
    if (temAntes) {
      if (antes) antes.textContent = this.#euros(v.compare_at_price);
      if (pct) pct.textContent = `🏷 ${pct.dataset.rotulo ?? ''} ${this.#euros(v.compare_at_price - v.price)}`.replace(/\s+/g, ' ').trim();
    }

    const fixaPreco = this.querySelector('[data-claro-fixa-preco]');
    if (fixaPreco) fixaPreco.textContent = this.#euros(v.price);

    const fixaAntes = this.querySelector('[data-claro-fixa-antes]');
    if (fixaAntes) {
      fixaAntes.hidden = !temAntes;
      if (temAntes) fixaAntes.textContent = this.#euros(v.compare_at_price);
    }

    const fixaPct = this.querySelector('[data-claro-fixa-pct]');
    if (fixaPct) {
      fixaPct.hidden = !temAntes;
      if (temAntes) {
        const desc = Math.round(((v.compare_at_price - v.price) / v.compare_at_price) * 100);
        fixaPct.textContent = `−${desc}%`;
      }
    }

    // Um artigo esgotado não se compra por aqui, tal como não se compra lá em cima
    if (this.fixaBotao) this.fixaBotao.disabled = !v.available;

    // Cada cartão de quantidade mostra o total, o antes e o que se poupa
    this.querySelectorAll('[role="radio"]').forEach((pack) => {
      const n = Number(pack.dataset.claroQtd) || 1;
      const extraPreco = Number(pack.dataset.extraPreco) || 0;
      const extraAntes = Number(pack.dataset.extraAntes) || extraPreco;
      const ehPack = Boolean(pack.dataset.claroPack);

      const total = ehPack ? v.price + extraPreco : v.price * n;
      const totalAntes = ehPack
        ? (temAntes ? v.compare_at_price : v.price) + extraAntes
        : (v.compare_at_price || 0) * n;

      const alvo = pack.querySelector('[data-claro-qtd-preco]');
      if (!alvo) {
        console.warn('claro-produto: falta o preço no cartão de', n);
        return;
      }
      alvo.textContent = this.#euros(total);

      const haPoupanca = totalAntes > total;

      const pAntes = pack.querySelector('[data-claro-qtd-antes]');
      if (pAntes) {
        pAntes.hidden = !haPoupanca;
        if (haPoupanca) pAntes.textContent = this.#euros(totalAntes);
      }

      const pPoupa = pack.querySelector('[data-claro-qtd-poupa]');
      if (!pPoupa) return;
      pPoupa.hidden = !haPoupanca;
      if (haPoupanca) {
        const rotulo = pPoupa.dataset.rotulo ?? '';
        pPoupa.textContent = `${rotulo} ${this.#euros(totalAntes - total)}`.trim();
      }
    });

    // Uma variante esgotada não se compra: o botão diz porquê
    const botao = this.querySelector('[data-claro-add]');
    const texto = this.querySelector('[data-claro-add-texto]');
    botao.disabled = !v.available;
    texto.textContent = v.available ? this.textoBotao : 'Esgotado';

    // Valores que não existem em nenhuma variante possível ficam apagados
    this.querySelectorAll('[data-claro-opcao]').forEach((grupo, i) => {
      const existe = (valor) => this.#variantes.some((outra) => outra.options[i] === valor);

      grupo.querySelectorAll('[data-claro-valor]').forEach((botao) => {
        botao.disabled = !existe(botao.dataset.claroValor);
      });

      grupo.querySelectorAll('[data-claro-sel] option').forEach((op) => {
        op.disabled = !existe(op.value);
      });
    });
  }
}

if (!customElements.get('claro-produto')) customElements.define('claro-produto', ClaroProduto);
